import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Menu, MenuDelete, MenuItem } from '../../../../../shared/menu/menu'
import { IconNotification, IconReload, IconShield, IconTransfer } from '../../../../../shared/icon/icon'
import { ModalDialog } from '../../../../../shared/component/modal-dialog'
import { Modal } from 'bootstrap'
import { ButtonDanger } from '../../../../../shared/legacy/button'
import { domainTone } from '../../../../../shared/component/status-pill'
import '../../../../../shared/sso-workspace/sso-workspace.scss'
import { dayOf } from '../../../../../shared/date/local-time'
import { OrgPicker } from '../../../../../shared/org-search/org-picker'

// Fixed, singleton modal target for Remove -- only one row's Override /
// Transfer / Remove flow is ever open at a time (see provider-domain-list.js),
// so it is rendered once outside the grid rather than once per row. Override
// and Transfer are plain portalled dialogs now (no bootstrap target needed);
// Remove still uses the bootstrap-backed ModalDialog.
export const PROVIDER_DOMAIN_REMOVE_MODAL = 'provider-domain-remove-modal'

// Pure, hook-free presentational row menu -- safe to call inline from
// ColumnMenu's `menu={row => ...}` cell renderer since it never calls a hook
// itself; every action is just a callback the table-owning component wires
// up (see provider-domain-list.js).
//
// `pinCheck` surfaces "check now" as an inline action instead of a kebab item:
// it is the one action an operator reaches for on nearly every row of the
// Domains grid (re-running the DNS check is how an at-risk domain gets
// resolved), it only re-reads DNS, and it is safely repeatable. Burying the
// page's most common action behind a two-click kebab was the complaint.
//
// It is opt-in rather than unconditional because this menu is ALSO rendered by
// the organization SSO tab (organization-sso.js) inside a plain <li>, not a
// table row. Outside a table the shared Menu turns a pinned item into a full
// text button rather than a compact icon -- which is exactly the arrangement
// that component deliberately avoids for inline lists. Leaving the prop off
// there keeps that page as it was.
//
// Everything else deliberately stays in the kebab:
//   override… / lift override  -- a policy escape hatch, rare, and its label
//                                 changes with the row's state
//   transfer…                  -- moves ownership between organizations, rare
//   notify members             -- sends real email to real people
//
// Require SSO is not here: it is a switch on the row with its explanation
// under it (shared/sso-workspace/require-sso.js, owner 2026-10-06) -- a menu
// item had nowhere to say what the setting does.
// delete keeps the codebase-wide default for MenuDelete (pinned inside a
// table) and is guarded by its own confirmation modal.
export const ProviderDomainRowMenu = ({
  domain,
  roleSysProvider,
  pinCheck,
  onCheck,
  onOpenOverride,
  onLiftOverride,
  onOpenTransfer,
  onNotify,
  onOpenRemove
}) => {
  const overridden = domainTone(domain) === 'overridden'

  return (
    // sentenceCase: the default kebab title-cases every word (see
    // shared/menu/menu.js), which mangled "require sso: off" into "Require
    // Sso: Off" -- wrong case throughout, and wrong even for SSO, which
    // should stay upper-cased. With it on, every `text` below is authored in
    // the exact case it should show.
    <Menu small sentenceCase>
      {/* pin stays undefined when pinCheck is not set, so Menu falls through to
          its own defaults rather than being forced unpinned. */}
      <MenuItem Icon={IconReload} text="Check now" onClick={() => onCheck(domain)} show={roleSysProvider.accessUpdate} pin={pinCheck || undefined} />

      {overridden
        ? <MenuItem Icon={IconShield} text="Lift override" onClick={() => onLiftOverride(domain)} show={roleSysProvider.accessUpdate} />
        : <MenuItem Icon={IconShield} text="Override…" onClick={() => onOpenOverride(domain)} show={roleSysProvider.accessUpdate} />}

      <MenuItem Icon={IconTransfer} text="Transfer…" onClick={() => onOpenTransfer(domain)} show={roleSysProvider.accessUpdate} />

      {domain.state === 'deactivated' &&
        <MenuItem Icon={IconNotification} text="Notify members" onClick={() => onNotify(domain)} show={roleSysProvider.accessUpdate} />}

      <MenuDelete text="Delete" onClick={() => onOpenRemove(domain)} show={roleSysProvider.accessDelete} />
    </Menu>
  )
}

// The page owns which row a modal is acting on; the modal follows it. A row
// set opens the dialog, and a row cleared -- after a successful save, or by a
// Cancel button -- closes it. Opening alone was the old behaviour: clearing
// the row emptied the body (rendered only while a row is set) and left a
// blank dialog on screen.
//
// Closing asks for an existing instance rather than creating one, so a modal
// that was never opened is left alone.
const useModalFollowsRow = (target, domain) => {
  useEffect(
    () => {
      if (domain) {
        Modal.getOrCreateInstance(`#${target}`).show()
        return
      }
      const instance = Modal.getInstance(`#${target}`)
      if (instance) instance.hide()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [domain]
  )
}

// The picker hands back local midnight at the START of the picked day, so
// "until Sep 25" ended the override as the 25th began -- a day earlier than
// anyone reading it expects. The override runs through the end of the day
// instead, in the admin's own timezone; the server stores the exact instant.
export const overrideUntilThrough = picked => {
  if (!picked) return null
  const end = new Date(picked)
  end.setHours(23, 59, 59, 999)
  return end
}

// Cancels on Escape, like every other workspace dialog (ConfirmDialog,
// UnbindDialog). Only wired up while a row is actually open.
const useEscapeCancels = (open, onCancel) => {
  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel && onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])
}

// domain is null when no row is being overridden. Rebuilt in the workspace's
// own dialog pattern (a portalled .sw-drawer/.sw-dialog, not the bootstrap
// modal ModalDialog wraps) so it picks up the workspace's tokens, sentence
// case and validate-on-submit convention instead of the bare bootstrap chrome
// (dark title bar, "required" shown before typing, "Through (Optional)").
//
// The reason field is focused as soon as `domain` is set: the old bootstrap
// modal never focused anything, so typing right after opening -- while its
// fade transition was still running -- landed nowhere.
export const ProviderDomainOverrideModal = ({ domain, enabled, onSubmit, onCancel }) => {
  const [reason, setReason] = useState('')
  const [until, setUntil] = useState('')
  const [hint, setHint] = useState(null)
  const [busy, setBusy] = useState(false)
  const reasonRef = useRef(null)

  // Keyed on `domain`'s reference, not its id: a different row opening (or
  // this one reopening after a refetch) resets the form, but an unrelated
  // re-render of the hosting page -- an async fetch settling elsewhere, e.g.
  // the SSO tab's entitlement load -- leaves the same reference and must not
  // wipe a half-typed reason.
  useEffect(() => {
    setReason('')
    setUntil('')
    setHint(null)
    setBusy(false)
    if (domain && reasonRef.current) reasonRef.current.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain])

  useEscapeCancels(Boolean(domain), onCancel)

  if (!domain) return null

  const handleCancel = () => onCancel && onCancel()
  const handleSubmit = async e => {
    e.preventDefault()
    if (!reason.trim()) { setHint('Say why, for the audit trail.'); return }
    setHint(null)
    setBusy(true)
    try {
      await onSubmit({ reason: reason.trim(), until: until ? overrideUntilThrough(new Date(`${until}T00:00:00`)) : null })
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center" onClick={e => { if (e.target === e.currentTarget) handleCancel() }}>
      <div className="sw-dialog" role="dialog" aria-modal="true" aria-labelledby="sw-override-title">
        <h2 className="h5" id="sw-override-title">Override {domain.domain}</h2>
        <p className="sw-hint">The domain routes without DNS proof until the date below, or until the override is lifted.</p>
        <form noValidate onSubmit={handleSubmit}>
          <div className="sw-field mb-2">
            <label className="sw-label" htmlFor="sw-override-reason">Reason</label>
            <input
              id="sw-override-reason"
              ref={reasonRef}
              className="form-control"
              value={reason}
              placeholder="Why, for example: their IT is changing DNS host"
              disabled={!enabled || busy}
              onChange={e => setReason(e.target.value)}
            />
            {hint && <div className="sw-required">{hint}</div>}
          </div>
          <div className="sw-field mb-3">
            <label className="sw-label" htmlFor="sw-override-until">Until (optional)</label>
            <input
              id="sw-override-until"
              type="date"
              className="form-control"
              value={until}
              min={dayOf(Date.now())}
              disabled={!enabled || busy}
              onChange={e => setUntil(e.target.value)}
            />
          </div>
          <div className="sw-dialog-actions">
            <button type="button" className="btn sw-btn" onClick={handleCancel}>Cancel</button>
            <button type="submit" className="btn sw-btn-primary" disabled={!enabled || busy}>Override</button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

// The owner a domain can be transferred to: an organization, or EducationXR.
const PLATFORM_HELD = { value: '', label: 'EducationXR (platform-held)' }

// Same dialog pattern as Override above. The new owner is picked from
// content's organization search (OrgPicker), never a list of every
// organization; the dialog itself takes focus when it opens.
export const ProviderDomainTransferModal = ({ domain, enabled, onSubmit, onCancel }) => {
  const [organizationID, setOrganizationID] = useState('')
  const [busy, setBusy] = useState(false)
  const dialogRef = useRef(null)

  useEffect(() => {
    setOrganizationID(domain && domain.organizationID ? String(domain.organizationID) : '')
    setBusy(false)
    if (domain && dialogRef.current) dialogRef.current.focus()
  }, [domain])

  useEscapeCancels(Boolean(domain), onCancel)

  if (!domain) return null

  const handleCancel = () => onCancel && onCancel()
  const handleTransfer = async () => {
    setBusy(true)
    try {
      await onSubmit(organizationID ? Number(organizationID) : null)
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center" onClick={e => { if (e.target === e.currentTarget) handleCancel() }}>
      <div className="sw-dialog" role="dialog" aria-modal="true" aria-labelledby="sw-transfer-title" tabIndex={-1} ref={dialogRef}>
        <h2 className="h5" id="sw-transfer-title">Transfer {domain.domain}</h2>
        <div className="sw-field mb-3">
          <label className="sw-label" htmlFor="provider-domain-transfer-organization">Owner</label>
          <OrgPicker
            id="provider-domain-transfer-organization"
            label="Transfer to organization"
            value={organizationID}
            allOption={PLATFORM_HELD}
            disabled={!enabled || busy}
            onChange={setOrganizationID}
          />
        </div>
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" onClick={handleCancel}>Cancel</button>
          <button type="button" className="btn sw-btn-primary" disabled={!enabled || busy} onClick={handleTransfer}>Transfer</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export const ProviderDomainRemoveModal = ({ domain, enabled, onConfirm, onCancel }) => {
  useModalFollowsRow(PROVIDER_DOMAIN_REMOVE_MODAL, domain)

  const handleHide = () => onCancel && onCancel()
  const handleConfirm = async () => await onConfirm()

  return (
    <ModalDialog target={PROVIDER_DOMAIN_REMOVE_MODAL} header="Remove domain" onHide={handleHide}>
      {domain &&
        <React.Fragment>
          <p>
            Remove {domain.domain}? Members will no longer be routed to SSO for this domain.
          </p>
          <div className="text-end">
            <button type="button" className="btn btn-secondary me-2" onClick={handleHide}>
              Cancel
            </button>
            <ButtonDanger text="Remove domain" disabled={!enabled} onClick={handleConfirm} />
          </div>
        </React.Fragment>}
    </ModalDialog>
  )
}
